// API utility for making authenticated requests to the backend
const API_URL =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : "http://localhost:3001";

async function getSessionToken() {
  const { account } = require("./appwrite");
  try {
    const jwt = await account.createJWT();
    return jwt.jwt;
  } catch {
    return null;
  }
}

async function apiRequest(path, options = {}) {
  const token = await getSessionToken();

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  // Handle raw responses (CSV downloads)
  if (options.rawResponse) {
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || `Request failed with status ${res.status}`);
    }
    return res;
  }

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || `Request failed with status ${res.status}`);
  }

  return data;
}

export const api = {
  // Add domain endpoints
  getDomains: () => apiRequest("/api/domains"),
  addDomain: (domain) =>
    apiRequest("/api/domains", {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),
  verifyDomain: (id) => apiRequest(`/api/domains/${id}/verify`),
  deleteDomain: (id) =>
    apiRequest(`/api/domains/${id}`, {
      method: "DELETE",
    }),

  // Links
  createLink: (body) =>
    apiRequest("/api/links", { method: "POST", body: JSON.stringify(body) }),

  getLinks: (page = 1, limit = 25, search = "", status = "", campaign_id = "") =>
    apiRequest(`/api/links?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&status=${status}&campaign_id=${encodeURIComponent(campaign_id)}`),

  getLink: (id) => apiRequest(`/api/links/${id}`),

  updateLink: (id, body) =>
    apiRequest(`/api/links/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteLink: (id) =>
    apiRequest(`/api/links/${id}`, { method: "DELETE" }),

  cloneLink: (id) =>
    apiRequest(`/api/links/${id}/clone`, { method: "POST" }),

  bulkAction: (action, ids, updates) =>
    apiRequest("/api/links/bulk", {
      method: "POST",
      body: JSON.stringify({ action, ids, updates }),
    }),

  importLinks: (rows, defaultCampaignId = "") =>
    apiRequest("/api/links/import", {
      method: "POST",
      body: JSON.stringify({ rows, defaultCampaignId }),
    }),

  exportLinks: ({ search = "", status = "", campaign_id = "" } = {}) =>
    apiRequest(
      `/api/links/export?search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}&campaign_id=${encodeURIComponent(campaign_id)}`,
      { rawResponse: true }
    ),

  // Analytics
  getAnalytics: (linkId, period = "30d") =>
    apiRequest(`/api/analytics/${linkId}?period=${period}`),

  getClickLog: (linkId, page = 1, limit = 50) =>
    apiRequest(`/api/analytics/${linkId}/clicks?page=${page}&limit=${limit}`),

  getDashboardSummary: () => apiRequest("/api/analytics/dashboard/summary"),

  getOverallAnalytics: (period = "30d") =>
    apiRequest(`/api/analytics/dashboard/overall?period=${period}`),

  getLinkConversions: (linkId, period = "30d") =>
    apiRequest(`/api/analytics/${linkId}/conversions?period=${period}`),

  exportCSV: (linkId) =>
    apiRequest(`/api/analytics/${linkId}/export`, { rawResponse: true }),

  // Preview
  getPreview: (url) =>
    apiRequest(`/api/preview?url=${encodeURIComponent(url)}`),

  // Bio Pages
  getBioPages: (page = 1, limit = 20) =>
    apiRequest(`/api/bio?page=${page}&limit=${limit}`),

  getBioPage: (id) => apiRequest(`/api/bio/${id}`),

  createBioPage: (body) =>
    apiRequest("/api/bio", { method: "POST", body: JSON.stringify(body) }),

  updateBioPage: (id, body) =>
    apiRequest(`/api/bio/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteBioPage: (id) =>
    apiRequest(`/api/bio/${id}`, { method: "DELETE" }),

  getBioPageAnalytics: (id, period = "7d") =>
    apiRequest(`/api/bio/${id}/analytics?period=${period}`),

  // Campaigns
  getCampaigns: () => apiRequest("/api/campaigns"),

  createCampaign: (body) =>
    apiRequest("/api/campaigns", { method: "POST", body: JSON.stringify(body) }),

  getCampaign: (id) => apiRequest(`/api/campaigns/${id}`),

  updateCampaign: (id, body) =>
    apiRequest(`/api/campaigns/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteCampaign: (id) => apiRequest(`/api/campaigns/${id}`, { method: "DELETE" }),

  getCampaignAnalytics: (id, period = "30d") =>
    apiRequest(`/api/analytics/campaign/${id}?period=${period}`),
};
